'use client'

import React, { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Personalidades, type NombreTarotista, type Personalidad } from '../data/personality'

interface PersonalitySelectorProps {
    selected: NombreTarotista | undefined
    onChange: (id: NombreTarotista) => void
}

const PersonalitySelector: React.FC<PersonalitySelectorProps> = ({ selected, onChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [hovered, setHovered] = useState<number | null>(null)

    // Convertir Record → Array
    const personalitiesArray: Personalidad[] = Object.values(Personalidades)
    const currentIndex = personalitiesArray.findIndex((p) => p.id === selected)
    const current = personalitiesArray[currentIndex]
    const others = personalitiesArray.filter((_, i) => i !== currentIndex)

    const select = useCallback(
        (p: Personalidad) => {
            onChange(p.id as NombreTarotista)
            setIsOpen(false)
        },
        [onChange]
    )

    const circlePosition = (index: number) => {
        const w = typeof window !== 'undefined' ? window.innerWidth : 400
        const angle = (index * 360) / Math.max(others.length, 1) - 90
        // ≤430px: cabe en pantalla; 520–767: tablet estrecha; ≥768 (p. ej. iPad mini): anillo amplio
        let radius = 150
        if (w <= 430) {
            radius = Math.min(92, Math.max(70, (w - 56) / 2))
        } else if (w < 520) {
            radius = 120
        } else if (w < 768) {
            radius = 150
        } else if (w < 1536) {
            radius = 182
        } else {
            radius = 198
        }
        const x = Math.cos((angle * Math.PI) / 180) * radius
        const y = Math.sin((angle * Math.PI) / 180) * radius
        return { x, y }
    }

    // GUARD: SI NO HAY SELECCIÓN VÁLIDA → NO RENDERIZA NADA
    if (!selected || !Personalidades[selected as NombreTarotista]) {
        return null // ← NO SE VE NADA
    }

    return (
        <>
            {/* ÍCONO FIJO: abajo a la derecha en móvil (evita tapar lectura), arriba en desktop */}
            <div
                className="fixed z-50 animate-fadeIn max-lg:bottom-[max(0.75rem,env(safe-area-inset-bottom,0px))] max-lg:right-3 max-lg:top-auto lg:top-20 lg:right-6 lg:bottom-auto"
                aria-label="Cambiar personalidad de la tirada"
            >
                <div className="relative cursor-pointer group touch-manipulation" onClick={() => setIsOpen(!isOpen)}>
                    {/* BORDE GIRATORIO DEGRADÉ */}
                    <div className="absolute inset-0 z-0 -m-1 rounded-full pointer-events-none animate-spin-subtle lg:-m-1.5">
                        <div className="w-full h-full rounded-full bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-200 p-[1px]" />
                    </div>

                    {/* IMAGEN CENTRAL (más chica en móvil) */}
                    <div className="relative z-10 h-16 w-16 overflow-hidden rounded-full shadow-2xl transition-transform max-lg:h-14 max-lg:w-14 lg:h-20 lg:w-20 xl:h-24 xl:w-24 group-hover:scale-110 group-active:scale-95">
                        <img
                            src={require(`../assets${current.image}`)}
                            alt={current.name}
                            className="object-cover w-full h-full"
                        />
                    </div>

                    {/* Nombre al hover (arriba del botón para que no se corte con el FAB abajo) */}
                    <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/80 px-3 py-1.5 text-xs text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        {current.name}
                    </div>
                </div>
            </div>

            {/* ANILLO DESPLEGADO HACIA ABAJO-IZQUIERDA */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Fondo oscuro */}
                        <div
                            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
                            onClick={() => setIsOpen(false)}
                        />

                        {/* Móvil: caja centrada en el viewport; desktop: esquina superior derecha */}
                        <div className="fixed z-50 h-80 w-80 max-md:bottom-28 max-md:left-1/2 max-md:right-auto max-md:top-auto max-md:h-80 max-md:w-[min(20rem,calc(100vw-1.5rem))] max-md:-translate-x-1/2 md:max-lg:bottom-28 md:max-lg:left-1/2 md:max-lg:right-auto md:max-lg:top-auto md:max-lg:h-[28rem] md:max-lg:w-[min(28rem,calc(100vw-1.5rem))] md:max-lg:-translate-x-1/2 lg:bottom-auto lg:left-auto lg:translate-x-0 lg:top-64 lg:right-0 lg:h-[28rem] lg:w-[28rem] xl:h-[30rem] xl:w-[30rem]">
                            <div className="absolute h-full w-full max-lg:left-0 max-lg:top-8 lg:left-10 lg:top-10">
                                {/* En ≤lg el origen del anillo va al centro de la caja (evita salida por la izquierda) */}
                                <div className="flex h-full w-full items-center justify-center lg:block">
                                    <div className="relative h-px w-px lg:h-full lg:w-full">
                                    {others.map((p, i) => {
                                        const pos = circlePosition(i)
                                        const isHovered = hovered === i

                                        return (
                                            <motion.div
                                                key={p.id}
                                                className="absolute left-0 top-0 cursor-pointer pointer-events-auto h-14 w-14 md:h-16 md:w-16"
                                                initial={{ scale: 0, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1, x: pos.x - 32, y: pos.y - 32 }}
                                                exit={{ scale: 0, opacity: 0 }}
                                                transition={{ delay: i * 0.015, type: 'spring', stiffness: 300 }}
                                                onClick={() => select(p)}
                                                onMouseEnter={() => setHovered(i)}
                                                onMouseLeave={() => setHovered(null)}
                                                style={{ zIndex: isHovered ? 100 : 10 }}
                                                whileHover={{ scale: 1.5, zIndex: 100 }}
                                            >
                                                {/* FLIP CARD (igual) */}
                                                <div className="relative w-full h-full [perspective:1000px]">
                                                    <motion.div
                                                        className="absolute inset-0 [transform-style:preserve-3d]"
                                                        animate={{ rotateY: isHovered ? 180 : 0 }}
                                                        transition={{ duration: 0.6, ease: 'easeInOut' }}
                                                    >
                                                        {/* Frente */}
                                                        <div className="absolute inset-0 rounded-full overflow-hidden border-4 border-white/60 shadow-xl [backface-visibility:hidden]">
                                                            <img
                                                                src={require(`../assets${p.image}`)}
                                                                alt={p.name}
                                                                className="object-cover w-full h-full"
                                                            />
                                                        </div>

                                                        {/* Dorso */}
                                                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-fuchsia-600 via-violet-600 to-cyan-600 flex items-center justify-center p-2 [backface-visibility:hidden] [transform:rotateY(180deg)] border-4 border-white/60 shadow-xl">
                                                            <span className="text-white text-[10px] font-bold text-center leading-tight drop-shadow-md">
                                                                {p.name}
                                                            </span>
                                                        </div>
                                                    </motion.div>
                                                </div>
                                            </motion.div>
                                        )
                                    })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </AnimatePresence>
        </>
    )
}

export default PersonalitySelector
